import { CardShell } from '@atlas/ui-components'
import { Chip } from '@atlas/ui-primitives'
import type { Prompt } from '@/domain/prompt.types'
import { PROMPT_TYPE_LABEL } from '@/domain/prompt.types'
import { PromptLifecyclePill } from './PromptLifecyclePill'

export interface PromptCardProps {
	prompt: Prompt
	/** Opens the prompt detail (kept router-free: the page passes navigation). */
	onOpen?: () => void
}

/**
 * Prompt result card: name · code · type/lifecycle/version chips.
 *
 * The `code` is shown in monospace beside the name because it is the handle agents
 * resolve by — an author needs to recognise it, not just the display name.
 */
export function PromptCard({ prompt, onOpen }: PromptCardProps) {
	return (
		<CardShell as="article" className="p-4 shadow-ds-sm transition-shadow hover:shadow-ds-md">
			<div className="flex items-start justify-between gap-3">
				<div className="flex-1">
					{onOpen ? (
						<button
							type="button"
							onClick={onOpen}
							className="block text-left text-sm font-medium leading-snug text-ds-text hover:underline"
						>
							{prompt.name}
						</button>
					) : (
						<p className="text-sm font-medium leading-snug text-ds-text">{prompt.name}</p>
					)}
					<p className="mt-1 font-mono text-ds-2xs text-ds-text-subtle">{prompt.code}</p>
					{prompt.description ? (
						<p className="mt-2 line-clamp-2 text-ds-sm text-ds-text-muted">{prompt.description}</p>
					) : null}
				</div>
				<PromptLifecyclePill lifecycle={prompt.lifecycle} />
			</div>

			<div className="mt-3 flex flex-wrap items-center gap-1.5">
				<Chip variant="muted" size="sm">
					{PROMPT_TYPE_LABEL[prompt.type]}
				</Chip>
				<Chip variant="muted" size="sm">
					{prompt.variables.length} {prompt.variables.length === 1 ? 'variable' : 'variables'}
				</Chip>
				{/* 0 reads as "never published" rather than as version zero. */}
				<Chip variant="muted" size="sm">
					{prompt.currentVersion === 0 ? 'Unpublished' : `v${prompt.currentVersion}`}
				</Chip>
			</div>
		</CardShell>
	)
}
